import React, { useEffect, useState } from "react";
import api, { formatTRY } from "@/lib/api";
import HotelLayout from "@/components/HotelLayout";
import { CalendarCheck, LogIn, LogOut, BedDouble, TrendingUp, Wallet, Hotel as HotelIcon, LoaderCircle, Phone, Mail } from "lucide-react";

const KpiCard = ({ icon: Icon, label, value, hint, color = "emerald", testid }) => (
    <div data-testid={testid} className={`p-4 rounded-2xl border bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800`}>
        <div className="flex items-start justify-between">
            <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
                <div className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{value}</div>
                {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
            </div>
            <div className={`p-2 rounded-xl bg-${color}-100 dark:bg-${color}-950/40 text-${color}-600 dark:text-${color}-400`}>
                <Icon className="h-4 w-4" />
            </div>
        </div>
    </div>
);

const Table = ({ items, type, empty }) => {
    if (!items || items.length === 0) {
        return <div className="text-sm text-slate-500 py-8 text-center">{empty}</div>;
    }
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid={`today-table-${type}`}>
                <thead className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 dark:border-slate-800">
                    <tr>
                        <th className="px-3 py-2 text-left font-bold">Misafir</th>
                        <th className="px-3 py-2 text-left font-bold">Oda</th>
                        <th className="px-3 py-2 text-left font-bold">Giriş — Çıkış</th>
                        <th className="px-3 py-2 text-left font-bold">Kişi</th>
                        <th className="px-3 py-2 text-right font-bold">Tutar</th>
                        <th className="px-3 py-2 text-center font-bold">İletişim</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((b, i) => (
                        <tr key={b.id || i} className="border-b border-slate-100 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/30">
                            <td className="px-3 py-2.5">
                                <div className="font-semibold text-slate-900 dark:text-white">{b.guest_name}</div>
                                {b.notes && <div className="text-[11px] text-amber-600 dark:text-amber-400 mt-0.5">💬 {b.notes}</div>}
                            </td>
                            <td className="px-3 py-2.5 text-slate-700 dark:text-slate-300">{b.room_type_name} {b.rooms > 1 && <span className="text-xs text-slate-500">x{b.rooms}</span>}</td>
                            <td className="px-3 py-2.5 text-slate-700 dark:text-slate-300 text-xs">{b.check_in} → {b.check_out}</td>
                            <td className="px-3 py-2.5 text-slate-700 dark:text-slate-300">{b.adults}Y {b.children > 0 && `${b.children}Ç`}</td>
                            <td className="px-3 py-2.5 text-right font-semibold text-slate-900 dark:text-white">{formatTRY(b.total)}</td>
                            <td className="px-3 py-2.5 text-center">
                                <div className="inline-flex gap-1">
                                    {b.guest_phone && <a href={`tel:${b.guest_phone}`} title={b.guest_phone} className="p-1.5 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-950/40 text-emerald-600"><Phone className="h-3.5 w-3.5" /></a>}
                                    {b.guest_email && <a href={`mailto:${b.guest_email}`} title={b.guest_email} className="p-1.5 rounded-lg hover:bg-sky-100 dark:hover:bg-sky-950/40 text-sky-600"><Mail className="h-3.5 w-3.5" /></a>}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default function HotelTodayPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/hotel/today").then((r) => setData(r.data)).finally(() => setLoading(false));
    }, []);

    if (loading || !data) {
        return <HotelLayout title="Bugün"><div className="flex items-center justify-center py-20"><LoaderCircle className="h-10 w-10 animate-spin text-emerald-500" /></div></HotelLayout>;
    }

    const { kpi, arrivals, departures, in_house, tomorrow_arrivals, today } = data;

    return (
        <HotelLayout title="Bugün — Operasyon" hotelName={data.hotel?.name} hotelImage={data.hotel?.main_image}>
            <div className="mb-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Bugün: {today}</div>

            {/* KPI bar */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
                <KpiCard icon={LogIn} label="Bugün Giriş" value={kpi.arrivals_count} color="emerald" testid="today-kpi-arrivals" />
                <KpiCard icon={LogOut} label="Bugün Çıkış" value={kpi.departures_count} color="amber" testid="today-kpi-departures" />
                <KpiCard icon={HotelIcon} label="İçeride" value={kpi.in_house_count} hint={`${kpi.free_rooms} boş`} color="sky" testid="today-kpi-inhouse" />
                <KpiCard icon={BedDouble} label="Doluluk" value={`${kpi.occupancy_pct}%`} hint={`${kpi.total_rooms} oda`} color="violet" testid="today-kpi-occ" />
                <KpiCard icon={TrendingUp} label="Bugün Rez." value={kpi.today_bookings} color="rose" testid="today-kpi-newbk" />
                <KpiCard icon={Wallet} label="Bugün Gelir" value={formatTRY(kpi.today_revenue)} color="emerald" testid="today-kpi-rev" />
            </div>

            {/* Arrivals */}
            <section className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 mb-4">
                <div className="flex items-center gap-2 mb-3">
                    <LogIn className="h-5 w-5 text-emerald-500" />
                    <h2 className="font-bold text-slate-900 dark:text-white">Bugün Giriş Yapacaklar ({arrivals.length})</h2>
                </div>
                <Table items={arrivals} type="arrivals" empty="Bugün giriş yok." />
            </section>

            {/* Departures */}
            <section className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 mb-4">
                <div className="flex items-center gap-2 mb-3">
                    <LogOut className="h-5 w-5 text-amber-500" />
                    <h2 className="font-bold text-slate-900 dark:text-white">Bugün Çıkış Yapacaklar ({departures.length})</h2>
                </div>
                <Table items={departures} type="departures" empty="Bugün çıkış yok." />
            </section>

            {/* In-house */}
            <section className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 mb-4">
                <div className="flex items-center gap-2 mb-3">
                    <HotelIcon className="h-5 w-5 text-sky-500" />
                    <h2 className="font-bold text-slate-900 dark:text-white">Şu An İçeride ({in_house.length})</h2>
                </div>
                <Table items={in_house} type="inhouse" empty="Şu anda otelde misafir yok." />
            </section>

            {/* Tomorrow arrivals preview */}
            <section className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5">
                <div className="flex items-center gap-2 mb-3">
                    <CalendarCheck className="h-5 w-5 text-violet-500" />
                    <h2 className="font-bold text-slate-900 dark:text-white">Yarın Giriş Yapacaklar ({tomorrow_arrivals.length})</h2>
                </div>
                <Table items={tomorrow_arrivals} type="tomorrow" empty="Yarın giriş kaydı yok." />
            </section>
        </HotelLayout>
    );
}
